import Close from '@mui/icons-material/Close'
import Refresh from '@mui/icons-material/Refresh'
import SupportAgent from '@mui/icons-material/SupportAgent'
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getClinicAssistantsDetailedForClinic } from 'src/supportPortal/services/clinicAssistantAdminService'
import ClinicAssistantCard from './ClinicAssistantCard'

/**
 * Roster + management drawer for the Clinic Assistants attached to a single clinic location.
 * Opened from the "Clinic Assistants" button on a location strip in ClinicsListing.
 *
 * Visible to (and the backend enforces) Clinic Success Admin and the respective Clinic Success
 * Manager. `canManageLifecycle` (CSA/admin) additionally unlocks approve + enable/disable.
 */
export default function ClinicAssistantsDrawer({
  open,
  clinicLocation,
  brands = [],
  mentors = [],
  canManageLifecycle = false,
  onClose,
}) {
  const clinicLocationId = clinicLocation?.id
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [availableManagers, setAvailableManagers] = useState([])
  const [errorMsg, setErrorMsg] = useState('')

  const brandNameById = useMemo(() => {
    const map = {}
    ;(brands || []).forEach((brand) => {
      if (brand?.id) {
        map[brand.id] = brand.title || brand.brandName || brand.slug || brand.id
      }
    })
    return map
  }, [brands])

  // Practitioner lookup so support-role managers (TCH- ids) resolve to name/pic/services.
  const mentorById = useMemo(() => {
    const map = {}
    ;(mentors || []).forEach((mentor) => {
      const id = mentor?.id || mentor?.uid || mentor?.userId
      if (id) map[id] = mentor
    })
    return map
  }, [mentors])

  const load = useCallback(async () => {
    if (!clinicLocationId) return
    setLoading(true)
    setErrorMsg('')
    try {
      const resp = await getClinicAssistantsDetailedForClinic(clinicLocationId)
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        const data = getDataMapFromCommonResponse(resp) || {}
        setRows(Array.isArray(data.rows) ? data.rows : [])
        setAvailableManagers(Array.isArray(data.availableManagers) ? data.availableManagers : [])
      } else {
        setRows([])
        setAvailableManagers([])
        setErrorMsg(
          resp?.response?.data?.message ||
            'Could not load clinic assistants. You may not have access to this location.',
        )
      }
    } catch (e) {
      console.error('load clinic assistants failed', e)
      setRows([])
      setAvailableManagers([])
      setErrorMsg('Could not load clinic assistants.')
    } finally {
      setLoading(false)
    }
  }, [clinicLocationId])

  useEffect(() => {
    if (open && clinicLocationId) {
      load()
    }
    if (!open) {
      setRows([])
      setAvailableManagers([])
      setErrorMsg('')
    }
  }, [open, clinicLocationId, load])

  const brandTitle =
    clinicLocation?.clinicBrandName || brandNameById[clinicLocation?.clinicBrandId] || ''

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: 820,
          maxWidth: '95vw',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            p: 3,
            background: 'linear-gradient(135deg, #1a2332 0%, #243044 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <SupportAgent />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} noWrap>
                Clinic Assistants
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }} noWrap>
                {clinicLocation?.title || clinicLocation?.name || 'Clinic location'}
                {brandTitle ? ` · ${brandTitle}` : ''}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {!loading && (
              <Chip
                size="small"
                label={`${rows.length} assistant${rows.length === 1 ? '' : 's'}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600 }}
              />
            )}
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={load} disabled={loading} sx={{ color: '#fff' }}>
                  <Refresh />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={onClose} sx={{ color: '#fff' }}>
              <Close />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2.5, bgcolor: '#f7f8fa' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress />
            </Box>
          ) : errorMsg ? (
            <Typography variant="body2" sx={{ color: 'error.main', p: 2 }}>
              {errorMsg}
            </Typography>
          ) : rows.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 6, color: 'text.secondary' }}>
              <SupportAgent sx={{ fontSize: 48, opacity: 0.3 }} />
              <Typography variant="body1" sx={{ mt: 1, fontWeight: 600 }}>
                No clinic assistants assigned
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                This clinic location has no clinic assistant assignments yet.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {rows.map((row) => (
                <ClinicAssistantCard
                  key={row?.assignment?.id || row?.user?.userId}
                  row={row}
                  brandNameById={brandNameById}
                  availableManagers={availableManagers}
                  mentorById={mentorById}
                  canManageLifecycle={canManageLifecycle}
                  onReload={load}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  )
}
